import { Autocomplete, Box, Checkbox, FormControlLabel, Slider, TextField, Typography } from '@mui/material'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import Context from '../Context'
import { apiJson } from '../utils/api'
import PickerDialog from './PickerDialog'
import theme from '../theme'
import { getSessionId } from '../utils/session'

/**
 * The filter panel the games board sits under, in both of its layouts. What is chosen is saved under the
 * key the board hands in, so a screen that wants its own selection can have one.
 *
 * Usage:
 *   const filters = useBoardFilters('searchFilters', events)
 *   ... {filters.card} ...
 *   events.filter(filters.matchesEvent)
 */

const FONT = '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'

export const GAP_MIN = -5
export const GAP_MAX = 5

// the labels the tables themselves use, so a chip and a column say the same word
export const MARKETS = ['1X2', 'AH', 'AOU']

// what the server will send at most, and therefore what the boards can show
export const MAX_EVENTS = 200

/**
 * "What starts soon" is asked in round numbers, not in dates — an hour before the off, the rest of the
 * session, the rest of the day. Presets answer that in one click where two date pickers would not.
 */
export const WINDOWS = [
  { label: '1h', hours: 1 },
  { label: '2h', hours: 2 },
  { label: '5h', hours: 5 },
  { label: '12h', hours: 12 },
  { label: '24h', hours: 24 },
  { label: '2d', hours: 48 },
  { label: 'Any', hours: null }
]

const WINDOW_HOURS = WINDOWS.map((option) => option.hours)

// the three pickers read as one set, so they are one size
const FIELD_WIDTH = '200px'

// one look for both chip rows: a pressed chip is filled, an idle one is the outline of the reset button
const chipSx = (active) => ({
  height: '30px',
  // a real button: reachable by Tab, pressed with Space or Enter, and announced as pressed or not
  appearance: 'none',
  padding: '0 13px',
  display: 'inline-flex',
  alignItems: 'center',
  border: `1px solid ${active ? theme.palette.primary.light : '#D6D6DC'}`,
  borderRadius: '2px',
  backgroundColor: active ? theme.palette.primary.light : 'white',
  color: active ? 'white' : '#6E7378',
  fontFamily: FONT,
  fontSize: '13px',
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': { borderColor: theme.palette.primary.light }
})

const groupLabelSx = { fontFamily: FONT, fontSize: '12px', color: '#6E7378', marginBottom: '6px' }

const fieldSx = {
  backgroundColor: 'white',
  borderRadius: '2px',
  '& .MuiOutlinedInput-input': { padding: '9px 12px', fontSize: '14px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C9C9D1' }
}

// what the two handles are actually asking for, in words
const gapLabel = ([low, high]) => {
  const show = (value) => `${value > 0 ? '+' : ''}${value}%`
  if (low === GAP_MIN && high === GAP_MAX) {
    return 'any'
  }
  if (low === GAP_MIN) {
    return `${show(high)} and below`
  }
  if (high === GAP_MAX) {
    return `${show(low)} and above`
  }
  return `${show(low)} to ${show(high)}`
}

/**
 * options.gap — whether this board asks about the distance to Pinnacle at all. The search board is built
 * around that question; the upcoming board is a schedule, where a slider comparing two prices has nothing
 * to compare and only takes up the line.
 */
export function useBoardFilters(storageKey, events, options = {}) {
  const withGap = options.gap !== false
  const { settings, setSettings, polledHost, username, setIsFavoriteChanging, isFavoriteChanging } = useContext(Context)

  const saved = settings[storageKey] || {}

  /**
   * Empty means no filter. All three pickers are typed into rather than scrolled through, because a
   * hundred and fifty leagues is a scroll, not a choice. One sport at a time — a board mixing tennis with
   * basketball is not comparing anything — but any number of its leagues and any number of teams.
   */
  const [ sport, setSport ] = useState(saved.sport || null)
  // saved.league is the single value this filter used to hold, kept working for anyone who saved one
  const [ leagues, setLeagues ] = useState(
    Array.isArray(saved.leagues) ? saved.leagues : (saved.league ? [saved.league] : [])
  )
  const [ teams, setTeams ] = useState(saved.teams || [])
  // a band rather than a floor: the interesting question is often "where are we worse", which a single
  // lower bound cannot ask. Either end sitting at its extreme means that side is simply not bounded.
  const [ gapRange, setGapRange ] = useState(
    Array.isArray(saved.gapRange) && saved.gapRange.length === 2 ? saved.gapRange : [GAP_MIN, GAP_MAX]
  )
  /**
   * The band the feed is asked for, which moves when the drag ends rather than with the handle. The
   * slider fires on every pixel, and the request now goes to the server: a drag across the width of the
   * control would otherwise be forty requests for forty answers nobody sees.
   */
  const [ gapAsked, setGapAsked ] = useState(gapRange)
  // nothing picked is every market, the same way an empty sport is every sport
  const [ markets, setMarkets ] = useState(Array.isArray(saved.markets) ? saved.markets : [])
  // hours from now; null is no horizon at all. A saved horizon this screen no longer offers — a window
  // that has since been dropped — reads as no horizon rather than as a chip nobody can see or unpress
  const [ within, setWithin ] = useState(WINDOW_HOURS.includes(saved.within) ? saved.within : null)
  // the stars set on the grid, which the feed carries back on every event it quotes
  const [ favOnly, setFavOnly ] = useState(Boolean(saved.favOnly))

  // set the moment anybody touches a picker: the account's own copy must not undo a live edit
  const touched = useRef(false)
  const edit = (apply) => (...args) => {
    touched.current = true
    apply(...args)
  }

  const toggleMarket = edit((market) => setMarkets((prev) =>
    (prev.includes(market) ? prev.filter((value) => value !== market) : [...prev, market])))

  /**
   * What the pickers offer comes from the dictionary rather than from the events on screen. It used to
   * be derived from the feed, which worked only while the feed carried everything; now the server sends
   * the page that was asked for, and a league list built from it would shrink to the choice that made it.
   *
   * It is read once and refreshed slowly: leagues and teams change when a fixture list changes, not
   * when a price moves.
   */
  const [ facets, setFacets ] = useState({ sports: [], leagues: [], teams: [], teamsBySport: {} })
  const [ facetsError, setFacetsError ] = useState('')

  /**
   * The user's starred games, so the picker's star can answer for a team that is not on the current
   * page. It is their own list, so it is small, and it is re-read whenever a star is touched anywhere.
   */
  const [ starred, setStarred ] = useState([])
  useEffect(() => {
    let alive = true
    const asked = new URLSearchParams({
      sessionId: getSessionId(), addAll: 'false', favOnly: 'true', minYield: '-1', bookmaker: 'Bovada'
    })
    apiJson(polledHost, `/odds-comparison01?${asked}`)
      .then((data) => {
        if (!alive) {
          return
        }
        const out = []
        for (const sportItem of data || []) {
          for (const leagueItem of sportItem.leagues || []) {
            out.push(...(leagueItem.eventList || []))
          }
        }
        setStarred(out)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [polledHost, isFavoriteChanging])

  useEffect(() => {
    let alive = true
    let timer = null
    /**
     * Empty pickers with no explanation are worse than a slow list, so a failure says so and is retried
     * in a few seconds. Once it has arrived, the dictionary is refreshed slowly: leagues and teams
     * change when a fixture list changes, not when a price moves.
     */
    const load = () => apiJson(polledHost, '/api/market/facets')
      .then((data) => {
        if (!alive) {
          return
        }
        setFacets(data)
        setFacetsError('')
        timer = setTimeout(load, 5 * 60 * 1000)
      })
      .catch((e) => {
        if (!alive) {
          return
        }
        setFacetsError(e.message || 'Could not load the league and team lists')
        timer = setTimeout(load, 10 * 1000)
      })
    load()
    return () => { alive = false; clearTimeout(timer) }
  }, [polledHost])

  const sports = useMemo(() => facets.sports || [], [facets])

  const leagueOptions = useMemo(
    () => [...new Set((facets.leagues || [])
      .filter((one) => !sport || one.sport === sport)
      .map((one) => one.league))],
    [facets, sport]
  )

  // narrowed to the chosen sport; the dictionary does not say which teams play in which league, so a
  // team outside the chosen leagues simply comes back with nothing, which is what choosing both means
  const teamOptions = useMemo(
    () => (sport ? (facets.teamsBySport || {})[sport] || [] : facets.teams || []),
    [facets, sport]
  )

  /**
   * What the pickers are showing, as one value — the same shape that is saved, so the two can be compared
   * without picking them apart field by field.
   */
  const chosen = useMemo(
    // always the same shape, gap band included. One board switches the slider on and off as its layout
    // changes, and a shape that followed it would drop the saved band the moment somebody looked at the
    // layout that has no slider
    () => ({ sport, leagues, teams, gapRange, markets, within, favOnly }),
    [sport, leagues, teams, gapRange, markets, within, favOnly]
  )

  /**
   * The last values these effects acted on. A flag saying "we have rendered once" is not enough: React
   * runs effects twice on mount in development, and a value restored from the account arrives as a change
   * like any other. Comparing against what was actually seen last tells a real edit from both of those.
   */
  const seenSport = useRef(sport)
  const seenLeagues = useRef(JSON.stringify(leagues))

  /**
   * A league or a team chosen under one sport means nothing under another, so changing the sport clears
   * both — but only when the sport really changed, never on the way in with saved filters.
   */
  /**
   * Changing the sport clears the league and the team in the same update rather than in an effect a paint
   * later: the board is rebuilt from these three together, and a frame where the new sport still carries
   * the old sport's leagues has nothing in it to show.
   */
  const chooseSport = edit((next) => {
    seenSport.current = next
    seenLeagues.current = '[]'
    setSport(next)
    setLeagues([])
    setTeams([])
  })

  /**
   * The chosen teams used to be pruned here to the ones playing in the newly chosen leagues, worked out
   * from the events on screen. That list is now the page the server sent, so pruning against it would
   * delete a team merely because it was not on this page. The two filters are ANDed upstream instead: a
   * team outside the chosen leagues comes back with nothing, which is what choosing both asks for.
   */
  const chooseLeagues = edit((next) => {
    seenLeagues.current = JSON.stringify(next)
    setLeagues(next)
  })

  /**
   * localStorage paints the first frame, but a browser that has never been here has nothing cached and
   * the account's own filters only arrive from /api/me a round trip later. They are adopted then — as
   * long as nobody has touched a picker in the meantime, because an answer from the server is no reason
   * to undo what the person in front of it just did.
   */
  const savedFilters = settings[storageKey]
  useEffect(() => {
    if (touched.current || !savedFilters) {
      return
    }
    const asChosen = { ...chosen, ...savedFilters }
    if (JSON.stringify(asChosen) === JSON.stringify(chosen)) {
      return
    }
    // told to the guards above first, so restoring a sport does not read as a change and wipe the rest
    // the same reading of the old single-league shape the first render does, or adopting would undo it
    const savedLeagues = Array.isArray(savedFilters.leagues)
      ? savedFilters.leagues
      : (savedFilters.league ? [savedFilters.league] : [])
    seenSport.current = savedFilters.sport || null
    seenLeagues.current = JSON.stringify(savedLeagues)
    setSport(savedFilters.sport || null)
    setLeagues(savedLeagues)
    setTeams(Array.isArray(savedFilters.teams) ? savedFilters.teams : [])
    setMarkets(Array.isArray(savedFilters.markets) ? savedFilters.markets : [])
    setWithin(WINDOW_HOURS.includes(savedFilters.within) ? savedFilters.within : null)
    setFavOnly(Boolean(savedFilters.favOnly))
    setGapAsked(Array.isArray(savedFilters.gapRange) && savedFilters.gapRange.length === 2
      ? savedFilters.gapRange
      : [GAP_MIN, GAP_MAX])
    setGapRange(Array.isArray(savedFilters.gapRange) && savedFilters.gapRange.length === 2
      ? savedFilters.gapRange
      : [GAP_MIN, GAP_MAX])
    // eslint-disable-next-line
  }, [savedFilters])

  /**
   * Written back to the account rather than kept on the page: the same filters then greet the trader on
   * whatever machine they sign in from, and survive a restart of anything. Only writes when something
   * actually changed, so this does not talk to the server on every render.
   */
  const waiting = useRef(null)
  const write = (value) => {
    waiting.current = null
    // a patch rather than a copy of the whole object: whatever else has changed in the meantime stays
    setSettings((current) => ({ ...current, [storageKey]: value }))
  }

  useEffect(() => {
    /**
     * Only an edit is worth writing. A board that was merely opened has filters that differ from what is
     * stored — an account that has never saved any, or a shape from an older version — and writing that
     * difference back turns "I looked at this screen" into "I chose these filters", which then rides the
     * unmount flush and overwrites what was actually saved.
     */
    if (!touched.current) {
      return undefined
    }
    if (JSON.stringify(chosen) === JSON.stringify(settings[storageKey] || {})) {
      return undefined
    }
    /**
     * Waited out rather than written straight away: dragging a slider handle passes through thirty
     * values on the way to the one that was meant, and each of those would otherwise be a request of
     * its own, with the last answer back not necessarily the last one sent.
     */
    waiting.current = chosen
    const timer = setTimeout(() => write(chosen), 400)
    return () => {
      clearTimeout(timer)
      // this value is no longer the one to write: the next run of this effect arms the newer one
      waiting.current = null
    }
    // eslint-disable-next-line
  }, [chosen])

  // leaving the page mid-pause — clicking a game right after a chip — must not lose the change
  useEffect(() => () => {
    if (waiting.current) {
      write(waiting.current)
    }
    // eslint-disable-next-line
  }, [])

  const reset = () => {
    touched.current = true
    setSport(null)
    setLeagues([])
    setTeams([])
    setGapRange([GAP_MIN, GAP_MAX])
    setGapAsked([GAP_MIN, GAP_MAX])
    setMarkets([])
    setWithin(null)
    setFavOnly(false)
  }

  const filtered = Boolean(sport) || leagues.length > 0 || teams.length > 0 || markets.length > 0
    || within !== null || favOnly || (withGap && (gapRange[0] > GAP_MIN || gapRange[1] < GAP_MAX))

  /**
   * Everything the boards ask of a fixture that is not about prices: the right sport, one of the chosen
   * leagues, one of the chosen teams, and starting inside the horizon. Kicked off fixtures never pass —
   * the feed goes on quoting them, and a match that started two days ago sorts to the top of a board
   * whose whole point is what is about to happen.
   */
  const matchesEvent = (event) => {
    if (Number(event.koTime) <= Date.now()) {
      return false
    }
    if (sport && event.sport !== sport) {
      return false
    }
    if (leagues.length && !leagues.includes(event.league)) {
      return false
    }
    if (teams.length && !teams.includes(event.home) && !teams.includes(event.away)) {
      return false
    }
    if (within !== null && Number(event.koTime) > Date.now() + within * 3600000) {
      return false
    }
    // the star belongs to the account, and the feed marks it per event, so nothing extra is asked for
    if (favOnly && !event.fav) {
      return false
    }
    return true
  }

  const wantsMarket = (market) => !markets.length || markets.includes(market)

  /**
   * Starring a team from the picker: every game it is playing that the feed is carrying gets the star,
   * or loses it. Favourites upstream are per fixture — there is no such thing as a starred team there —
   * so this is that list of fixtures, done in one go, which is what somebody looking a team up by name
   * is usually after.
   */
  const teamStar = useMemo(() => {
    /**
     * The team's fixtures are asked for rather than looked up on screen: the feed now answers with the
     * page the filters selected, and the team being starred is usually one nobody has selected yet.
     */
    const fixturesOf = async (team) => {
      const asked = new URLSearchParams({
        sessionId: getSessionId(), addAll: 'false', favOnly: 'false', minYield: '-1',
        bookmaker: 'Bovada', teams: team, limit: String(MAX_EVENTS)
      })
      const data = await apiJson(polledHost, `/odds-comparison01?${asked}`)
      const out = []
      for (const sportItem of data || []) {
        for (const leagueItem of sportItem.leagues || []) {
          for (const event of leagueItem.eventList || []) {
            out.push({ ...event, sport: sportItem.sport })
          }
        }
      }
      return out
    }

    return {
      /**
       * Drawn on every row of the list, so it cannot ask the server per team. It reads the starred games
       * fetched once above, falling back to what is on screen: a team is starred when every game of it
       * that we know about is starred, which is what the star meant before the board started answering
       * with a page instead of the whole book.
       */
      isOn: (team) => {
        const known = (starred || []).filter((event) => event.home === team || event.away === team)
        if (known.length) {
          return true
        }
        const fixtures = (events || []).filter((event) => event.home === team || event.away === team)
        return fixtures.length > 0 && fixtures.every((event) => event.fav)
      },
      toggle: async (team, wanted) => {
        const sessionId = getSessionId()
        const fixtures = await fixturesOf(team)
        await Promise.all(fixtures.map((event) => apiJson(
          polledHost,
          `/odds-comparison-change-fav?sessionId=${sessionId}&home=${encodeURIComponent(event.home)}`
            + `&away=${encodeURIComponent(event.away)}&koTime=${event.koTime}&add=${wanted}`
            + (event.sport ? `&sport=${encodeURIComponent(event.sport)}` : '')
        )))
        setIsFavoriteChanging((previous) => !previous)
      }
    }
    // eslint-disable-next-line
  }, [events, starred, username, polledHost])

  /**
   * An end left at its extreme is no bound at all rather than "at least minus five", which would quietly
   * drop the legs priced further from Pinnacle than the slider can reach.
   */
  const inGap = (gap) => {
    if (!withGap) {
      return true
    }
    const [ low, high ] = gapRange
    if (low > GAP_MIN && gap < low) {
      return false
    }
    if (high < GAP_MAX && gap > high) {
      return false
    }
    return true
  }

  const card = (
    <Box>
      <Box sx={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Autocomplete
          size="small"
          autoHighlight
          options={sports}
          value={sport}
          onChange={(event, next) => chooseSport(next)}
          sx={{ width: FIELD_WIDTH }}
          renderInput={(params) => (
            <TextField {...params} label="Sport" placeholder="Any sport" sx={fieldSx} />
          )}
        />

        {/* both of these are long lists picked several at a time: a field that grew a chip per choice
            pushed the whole row down the page, so they open in a window of their own instead */}
        <PickerDialog
          label="League"
          plural="leagues"
          options={leagueOptions}
          value={leagues}
          onChange={chooseLeagues}
          width={FIELD_WIDTH}
        />

        <PickerDialog
          label="Teams"
          plural="teams"
          options={teamOptions}
          value={teams}
          onChange={edit((next) => setTeams(next))}
          width={FIELD_WIDTH}
          star={teamStar}
        />

        {/* the stars are set on the league grid; this only decides whether to look at them alone */}
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={favOnly}
              onChange={edit((event) => setFavOnly(event.target.checked))}
              sx={{ color: '#9A9EA3', '&.Mui-checked': { color: theme.palette.primary.light } }}
            />
          }
          label="Favorites only"
          sx={{ marginRight: 0, '& .MuiFormControlLabel-label': { fontFamily: FONT, fontSize: '13px', color: '#6E7378' } }}
        />

        {/* the same height as the fields it sits beside, so the row reads as one line of controls */}
        <Box
          component="button"
          type="button"
          onClick={reset}
          sx={{
            height: '40px', border: '1px solid #D6D6DC', backgroundColor: 'white', borderRadius: '2px',
            padding: '0 18px', fontFamily: FONT, fontSize: '13px',
            color: filtered ? theme.palette.secondary.main : '#9A9EA3', cursor: 'pointer',
            '&:hover': { borderColor: theme.palette.secondary.main }
          }}
        >
          Reset filters
        </Box>

        {facetsError && (
          <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: theme.palette.secondary.main }}>
            The league and team lists are unavailable — {facetsError}
          </Typography>
        )}
      </Box>

      {/* the three narrower filters share the second line, each under its own label */}
      <Box sx={{ display: 'flex', gap: '34px', flexWrap: 'wrap', alignItems: 'flex-start', marginTop: '16px' }}>
        <Box>
          <Typography sx={groupLabelSx}>Market</Typography>
          <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {MARKETS.map((market) => (
              <Box
                key={market}
                component="button"
                type="button"
                aria-pressed={markets.includes(market)}
                onClick={() => toggleMarket(market)}
                sx={chipSx(markets.includes(market))}
              >
                {market}
              </Box>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography sx={groupLabelSx}>Starts within</Typography>
          <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {WINDOWS.map((option) => (
              <Box
                key={option.label}
                component="button"
                type="button"
                aria-pressed={within === option.hours}
                onClick={edit(() => setWithin(option.hours))}
                sx={chipSx(within === option.hours)}
              >
                {option.label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* the thumbs and the end labels reach past the track, so the box is padded to hold them */}
        {withGap && (
        <Box sx={{ width: '300px', maxWidth: '100%', paddingRight: '10px', boxSizing: 'border-box' }}>
          <Typography sx={groupLabelSx}>
            Gap vs Pinnacle, {gapLabel(gapRange)}
          </Typography>
          <Slider
            value={gapRange}
            min={GAP_MIN}
            max={GAP_MAX}
            step={0.5}
            disableSwap
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value > 0 ? '+' : ''}${value}%`}
            marks={[
              { value: GAP_MIN, label: `${GAP_MIN}` },
              { value: 0, label: '0' },
              { value: GAP_MAX, label: `+${GAP_MAX}` }
            ]}
            onChange={edit((event, next) => setGapRange(next))}
            onChangeCommitted={(event, next) => setGapAsked(next)}
            sx={{ color: theme.palette.primary.light, padding: '9px 0' }}
          />
        </Box>
        )}
      </Box>
    </Box>
  )

  /**
   * What the feed has to be asked for. The sport, leagues, teams and horizon are answered upstream now,
   * so they travel as parameters; the markets and the gap band are read off prices the feed already
   * carries, so they stay here.
   */
  const query = useMemo(() => {
    // an end left at its extreme is no bound at all, and the gap band only means anything where our own
    // price sits beside pinnacle's - the layout without that column does not send it
    const [ low, high ] = gapAsked
    return {
      sport,
      leagues,
      teams,
      within,
      // the market chips travel only where they mean "keep the games that have this market priced" -
      // the selections layout. On the fixtures layout they choose which columns are drawn, and sending
      // them would delete every fixture the two books do not both quote on that market
      markets: withGap ? markets : [],
      gapMin: withGap && low > GAP_MIN ? low : null,
      gapMax: withGap && high < GAP_MAX ? high : null,
      favOnly: favOnly ? 'true' : null,
      // a game that has kicked off is not on these boards at all; the server drops it rather than the
      // table quietly skipping the row after it has crossed the wire
      grace: 0,
      limit: MAX_EVENTS
    }
  }, [sport, leagues, teams, within, markets, gapAsked, favOnly, withGap])

  return {
    sport, leagues, teams, markets, within, gapRange, favOnly,
    card, reset, filtered, query,
    matchesEvent, wantsMarket, inGap
  }
}

export default useBoardFilters
